import React, { useCallback, useEffect, useMemo, useState } from 'react'
import apiClient from '../../services/api'
import { useToast } from '../../context/ToastContext'
import LoadingSpinner from '../../components/LoadingSpinner'
import defaultDoctors from '../../data/defaultDoctors'

const statusFilters = [
  { key: 'all', label: 'All Requests' },
  { key: 'pending', label: 'Pending' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'rejected', label: 'Rejected' }
]

const statusClassMap = {
  pending: 'admin-status-pending',
  confirmed: 'admin-status-confirmed',
  rejected: 'admin-status-rejected'
}

const normalizeText = (value = '') =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const serviceKeywords = (serviceTitle = '') => {
  const normalized = normalizeText(serviceTitle)

  if (normalized.includes('general') || normalized.includes('checkup')) {
    return []
  }

  if (normalized.includes('cardio')) {
    return ['cardio', 'cardiology', 'heart']
  }

  if (normalized.includes('pediatric')) {
    return ['pediatric', 'pediatrics', 'child']
  }

  if (normalized.includes('orthopedic') || normalized.includes('orthopedics') || normalized.includes('ortho')) {
    return ['orthopedic', 'orthopedics', 'ortho', 'joint']
  }

  if (normalized.includes('dental') || normalized.includes('dent')) {
    return ['dental', 'dentistry', 'oral']
  }

  return normalized.split(' ').filter(Boolean)
}

const matchDoctorsToService = (serviceTitle, doctors = []) => {
  const keywords = serviceKeywords(serviceTitle)

  if (keywords.length === 0) {
    return doctors
  }

  const matches = doctors.filter((doctor) => {
    const specialty = normalizeText(doctor.specialty)
    return keywords.some((keyword) => specialty.includes(keyword) || keyword.includes(specialty))
  })

  return matches.length > 0 ? matches : doctors
}

const mergeDoctors = (apiDoctors = []) => {
  const activeDoctors = apiDoctors.filter((doctor) => doctor.active)
  const normalizedDoctorNames = new Set(activeDoctors.map((doctor) => normalizeText(doctor.name)))

  const missingDefaults = defaultDoctors.filter(
    (doctor) => !normalizedDoctorNames.has(normalizeText(doctor.name))
  )

  return [...activeDoctors, ...missingDefaults]
}

const AdminAppointments = () => {
  const [appointments, setAppointments] = useState([])
  const [doctors, setDoctors] = useState([])
  const [doctorDrafts, setDoctorDrafts] = useState({})
  const [loading, setLoading] = useState(true)
  const [statusFilter, setStatusFilter] = useState('all')
  const { addToast } = useToast()

  const doctorMap = useMemo(
    () => doctors.reduce((acc, doctor) => ({ ...acc, [doctor.name]: doctor }), {}),
    [doctors]
  )

  const loadAppointments = useCallback(async () => {
    try {
      const [appointmentsResponse, doctorsResponse] = await Promise.all([
        apiClient.get('/api/appointments'),
        apiClient.get('/api/cms/doctors')
      ])

      setAppointments(appointmentsResponse.data)
      setDoctors(mergeDoctors(doctorsResponse.data))
      setDoctorDrafts(
        appointmentsResponse.data.reduce((acc, appointment) => {
          acc[appointment._id] = appointment.doctorName || appointment.requestedDoctorName || ''
          return acc
        }, {})
      )
    } catch (error) {
      console.error('Error loading appointments', error)
      addToast('Error loading appointments', 'danger')
    } finally {
      setLoading(false)
    }
  }, [addToast])

  useEffect(() => {
    loadAppointments()
  }, [loadAppointments])

  const updateStatus = async (appointment, status) => {
    const selectedDoctorName = doctorDrafts[appointment._id] || appointment.doctorName || ''
    const selectedDoctor = doctorMap[selectedDoctorName]

    if (status === 'confirmed' && !selectedDoctorName) {
      addToast('Please assign a doctor before confirming this appointment.', 'warning')
      return
    }

    try {
      const response = await apiClient.put(`/api/appointments/${appointment._id}`, {
        status,
        doctorName: selectedDoctorName,
        doctorSpecialty: selectedDoctor?.specialty || appointment.doctorSpecialty || ''
      })

      setAppointments((prev) =>
        prev.map((item) => (item._id === appointment._id ? response.data : item))
      )
      setDoctorDrafts((prev) => ({
        ...prev,
        [appointment._id]: response.data.doctorName || ''
      }))
      addToast(`Appointment ${status}.`, 'success')
    } catch (error) {
      console.error('Error updating status', error)
      addToast(error.response?.data?.message || 'Error updating status', 'danger')
    }
  }

  const deleteAppointment = async (id) => {
    if (!window.confirm('Delete this appointment?')) return
    try {
      await apiClient.delete(`/api/appointments/${id}`)
      setAppointments((prev) => prev.filter((appointment) => appointment._id !== id))
    } catch (error) {
      console.error('Error deleting appointment', error)
      addToast('Error deleting appointment', 'danger')
    }
  }

  if (loading) return <LoadingSpinner />

  const filteredAppointments = appointments.filter((appointment) =>
    statusFilter === 'all' ? true : appointment.status === statusFilter
  )

  const filterCount = (key) =>
    key === 'all'
      ? appointments.length
      : appointments.filter((appointment) => appointment.status === key).length

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h2>Appointments</h2>
          <p>
            Review richer patient details, assign the final doctor, and confirm requests with a proper appointment number and polished confirmation email.
          </p>
        </div>
      </div>

      <div className="admin-toolbar">
        <div className="admin-filter-chips">
          {statusFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={`admin-filter-chip${statusFilter === filter.key ? ' is-active' : ''}`}
              onClick={() => setStatusFilter(filter.key)}
            >
              {filter.label}
              <span className="admin-filter-chip-count">{filterCount(filter.key)}</span>
            </button>
          ))}
        </div>
        <div className="admin-note">{filteredAppointments.length} appointment{filteredAppointments.length === 1 ? '' : 's'} shown</div>
      </div>

      <div className="table-responsive">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Patient</th>
              <th>Booking</th>
              <th>Schedule</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAppointments.map((appointment) => {
              const matchedDoctors = matchDoctorsToService(appointment.service, doctors)

              return (
                <tr key={appointment._id}>
                  <td>
                    <div className="admin-table-cell-stack">
                      <span className="admin-table-label">{appointment.name}</span>
                      <span className="admin-table-meta">{appointment.age} years, {appointment.gender}</span>
                      <span className="admin-table-meta">{appointment.email}</span>
                      <span className="admin-table-meta">{appointment.phone}</span>
                      <span className="admin-table-meta">{appointment.address}</span>
                    </div>
                  </td>
                  <td>
                    <div className="admin-table-cell-stack">
                      <span className="admin-table-label">{appointment.service}</span>
                      <span className="admin-table-meta">
                        {appointment.appointmentNumber || 'Appointment number pending confirmation'}
                      </span>
                      <label className="admin-table-meta" htmlFor={`doctor-${appointment._id}`}>
                        Assigned doctor
                      </label>
                      <select
                        id={`doctor-${appointment._id}`}
                        className="form-select form-select-sm"
                        value={doctorDrafts[appointment._id] || ''}
                        onChange={(event) =>
                          setDoctorDrafts((prev) => ({ ...prev, [appointment._id]: event.target.value }))
                        }
                      >
                        <option value="">Select doctor</option>
                        {matchedDoctors.map((doctor) => (
                          <option key={`${appointment._id}-${doctor.name}`} value={doctor.name}>
                            {doctor.name} - {doctor.specialty}
                          </option>
                        ))}
                      </select>
                      <span className="admin-table-meta">
                        Patient requested: {appointment.requestedDoctorName || 'Any available doctor'}
                      </span>
                      {appointment.message && (
                        <span className="admin-table-meta">Notes: {appointment.message}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="admin-table-cell-stack">
                      <span className="admin-table-label">{new Date(appointment.date).toLocaleDateString()}</span>
                      <span className="admin-table-meta">{appointment.time}</span>
                      {appointment.confirmedAt && (
                        <span className="admin-table-meta">Confirmed on {new Date(appointment.confirmedAt).toLocaleString()}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`admin-status-badge ${statusClassMap[appointment.status] || 'admin-status-new'}`}>
                      {appointment.status}
                    </span>
                  </td>
                  <td>
                    <div className="admin-action-group">
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => updateStatus(appointment, 'confirmed')}
                        disabled={appointment.status === 'confirmed'}
                      >
                        {appointment.status === 'confirmed' ? 'Confirmed' : 'Confirm'}
                      </button>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => updateStatus(appointment, 'rejected')}
                        disabled={appointment.status === 'rejected'}
                      >
                        {appointment.status === 'rejected' ? 'Rejected' : 'Reject'}
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => deleteAppointment(appointment._id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default AdminAppointments
